import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrders } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useOffline } from '../context/OfflineContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/StatusBadge';
import { formatOrderNo, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { ClipboardList, MapPin, Calendar, ChevronRight, WifiOff, Lock, Play } from 'lucide-react';

export const EmployeeOrdersPage = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isOfflineData, setIsOfflineData] = useState(false);
    const { user } = useAuth();
    const { isOnline, cacheOrders, getCachedOrders } = useOffline();
    const navigate = useNavigate();

    useEffect(() => {
        loadOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadOrders = async () => {
        try {
            const res = await getOrders();
            // Filter for ASSIGNED and IN_PROGRESS only, sorted by order_no (sequence)
            const myOrders = res.data
                .filter(o => 
                    o.assigned_to_user_id === user?.id && 
                    ['ASSIGNED', 'IN_PROGRESS'].includes(o.status)
                )
                .sort((a, b) => a.order_no - b.order_no); // Sort by order number (admin's sequence)
            setOrders(myOrders);
            setIsOfflineData(false);
            
            // Cache orders for offline access
            await cacheOrders(myOrders);
        } catch (err) {
            // Try to load from cache if offline
            if (!isOnline) {
                const cachedOrders = await getCachedOrders();
                if (cachedOrders && cachedOrders.length > 0) {
                    setOrders(cachedOrders);
                    setIsOfflineData(true);
                    toast.info('Offline-Modus: Daten aus dem Cache geladen');
                    setLoading(false);
                    return;
                }
            }
            toast.error('Fehler beim Laden der Aufträge');
        } finally {
            setLoading(false);
        }
    };

    // Check if an order can be worked on (first ASSIGNED or any IN_PROGRESS)
    const canWorkOnOrder = (order, index) => {
        // Admins can work on any order without sequence restriction
        if (user?.role === 'ADMIN') return true;
        
        // If order is already in progress, can always work on it
        if (order.status === 'IN_PROGRESS') return true;
        
        // Check if there's any order already in progress
        const hasInProgress = orders.some(o => o.status === 'IN_PROGRESS');
        if (hasInProgress) return false;
        
        // Only the first ASSIGNED order can be started
        const firstAssignedIndex = orders.findIndex(o => o.status === 'ASSIGNED');
        return index === firstAssignedIndex;
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    // Separate in-progress and assigned orders
    const inProgressOrders = orders.filter(o => o.status === 'IN_PROGRESS');
    const assignedOrders = orders.filter(o => o.status === 'ASSIGNED');

    return (
        <div className="p-4 md:p-6 space-y-4 pb-24" data-testid="employee-orders-page">
            {/* Offline Banner */}
            {isOfflineData && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
                    <WifiOff className="h-5 w-5 text-amber-600" />
                    <div>
                        <p className="font-medium text-amber-800">Offline-Modus</p>
                        <p className="text-sm text-amber-600">Diese Daten stammen aus dem Cache</p>
                    </div>
                </div>
            )}
            
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-xl md:text-2xl font-bold">Meine Aufträge</h1>
                <p className="text-muted-foreground">{orders.length} offene Aufträge</p>
            </div>

            {/* Orders List */}
            {orders.length === 0 ? (
                <Card>
                    <CardContent className="empty-state py-16">
                        <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-lg font-medium">Keine offenen Aufträge</p>
                        <p className="text-muted-foreground">Sie haben derzeit keine zugewiesenen Aufträge</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {/* In Progress Orders - Always shown first */}
                    {inProgressOrders.length > 0 && (
                        <div className="space-y-3">
                            <h2 className="text-sm font-semibold text-amber-700 uppercase tracking-wider flex items-center gap-2">
                                <Play className="h-4 w-4" />
                                Aktuell in Bearbeitung
                            </h2>
                            {inProgressOrders.map((order) => (
                                <Card 
                                    key={order.id} 
                                    className="order-card-mobile cursor-pointer border-amber-300 bg-amber-50/50"
                                    onClick={() => navigate(`/my-orders/${order.id}`)}
                                    data-testid={`order-card-${order.order_no}`}
                                >
                                    <CardContent className="p-0">
                                        <div className="p-4">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="order-no text-primary">{formatOrderNo(order.order_no)}</span>
                                                <StatusBadge status={order.status} />
                                            </div>
                                            <h3 className="font-semibold text-lg mb-2">{order.title}</h3>
                                            <p className="text-sm text-muted-foreground mb-3">{order.customer?.name}</p>
                                            
                                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                                {(order.service_address || order.customer?.billing_address) && (
                                                    <div className="flex items-center gap-1">
                                                        <MapPin className="h-4 w-4" />
                                                        <span className="truncate max-w-[200px]">
                                                            {order.service_address || order.customer?.billing_address}
                                                        </span>
                                                    </div>
                                                )}
                                                {order.planned_date && (
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="h-4 w-4" />
                                                        <span>{formatDate(order.planned_date)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="border-t px-4 py-3 bg-amber-100 flex items-center justify-between">
                                            <span className="text-sm font-medium text-amber-800">
                                                Jetzt weiterarbeiten
                                            </span>
                                            <ChevronRight className="h-5 w-5 text-amber-600" />
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* Assigned Orders - In sequence (or free order for admins) */}
                    {assignedOrders.length > 0 && (
                        <div className="space-y-3">
                            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                                {user?.role === 'ADMIN' ? `Zugewiesene Aufträge (${assignedOrders.length})` : `Warteschlange (${assignedOrders.length} Aufträge)`}
                            </h2>
                            {assignedOrders.map((order, index) => {
                                const canWork = canWorkOnOrder(order, orders.indexOf(order));
                                const isNext = index === 0 && !inProgressOrders.length;
                                const isAdmin = user?.role === 'ADMIN';
                                
                                return (
                                    <Card 
                                        key={order.id} 
                                        className={`order-card-mobile ${canWork ? 'cursor-pointer' : 'opacity-60'} ${isNext && !isAdmin ? 'border-primary border-2' : ''}`}
                                        onClick={() => canWork && navigate(`/my-orders/${order.id}`)}
                                        data-testid={`order-card-${order.order_no}`}
                                    >
                                        <CardContent className="p-0">
                                            <div className="p-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                        {!isAdmin && (
                                                            <span className="bg-primary text-primary-foreground text-xs font-bold px-2 py-1 rounded">
                                                                #{index + 1}
                                                            </span>
                                                        )}
                                                        <span className="order-no text-primary">{formatOrderNo(order.order_no)}</span>
                                                    </div>
                                                    <StatusBadge status={order.status} />
                                                </div>
                                                <h3 className="font-semibold text-lg mb-2">{order.title}</h3>
                                                <p className="text-sm text-muted-foreground mb-3">{order.customer?.name}</p>
                                                
                                                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                                    {(order.service_address || order.customer?.billing_address) && (
                                                        <div className="flex items-center gap-1">
                                                            <MapPin className="h-4 w-4" />
                                                            <span className="truncate max-w-[200px]">
                                                                {order.service_address || order.customer?.billing_address}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {order.planned_date && (
                                                        <div className="flex items-center gap-1">
                                                            <Calendar className="h-4 w-4" />
                                                            <span>{formatDate(order.planned_date)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Show queue info only for non-admins */}
                                            {!isAdmin && (
                                                <div className={`border-t px-4 py-3 flex items-center justify-between ${isNext ? 'bg-primary/10' : 'bg-muted/30'}`}>
                                                    {isNext ? (
                                                        <>
                                                            <span className="text-sm font-medium text-primary">
                                                                Als Nächstes starten
                                                            </span>
                                                            <ChevronRight className="h-5 w-5 text-primary" />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                                                                <Lock className="h-4 w-4" />
                                                                Warten auf vorherige Aufträge
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            {/* For admins - just show clickable indicator */}
                                            {isAdmin && (
                                                <div className="border-t px-4 py-3 flex items-center justify-between bg-muted/30">
                                                    <span className="text-sm font-medium text-primary">
                                                        Auftrag öffnen
                                                    </span>
                                                    <ChevronRight className="h-5 w-5 text-primary" />
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EmployeeOrdersPage;
